"use client";

import { createContext, useCallback, useContext } from "react";

import type { NavigateOptions } from "@ala/core/navigation";
import { useRouter } from "@ala/core/navigation";

import { currentConfig } from "../constants";
import { resolveRelativePath } from "./resolve-relative-path";

export type Stage = "leaving" | "entering" | "none";

export interface TransitionOptions {
  animate?: boolean;
}

export type TransitionRouterNavigateMethod = (href: string, options?: NavigateOptions, transitionOptions?: TransitionOptions) => void;

interface TransitionRouterContextValue {
  push: TransitionRouterNavigateMethod;
  replace: TransitionRouterNavigateMethod;
  stage: Stage;
}

const TransitionRouterContext = createContext<TransitionRouterContextValue | null>(null);

export const TransitionRouterContextProvider = TransitionRouterContext.Provider;

export const useTransitionRouter = () => {
  const nextRouter = useRouter();
  const context = useContext(TransitionRouterContext);

  const pushWithAugmentedUrl = useCallback(
    (url, options) => {
      return nextRouter.push(resolveRelativePath(url, currentConfig), options);
    },
    [nextRouter],
  ) as typeof nextRouter.push;

  const replaceWithAugmentedUrl = useCallback(
    (url, options) => {
      return nextRouter.replace(resolveRelativePath(url, currentConfig), options);
    },
    [nextRouter],
  ) as typeof nextRouter.replace;

  if (!context) {
    return {
      stage: "none",
      push: pushWithAugmentedUrl,
      replace: replaceWithAugmentedUrl,
    };
  }

  return context;
};
